import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api.js';
import toast from 'react-hot-toast';
import { AlertCircle, RotateCcw } from 'lucide-react';
import Swal from 'sweetalert2';

export default function DevolucionSolicitudModal({ solicitud, onClose, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [montoDevolucion, setMontoDevolucion] = useState(solicitud?.total_cotizacion ? Math.round(Number(solicitud.total_cotizacion)) : '');
  const [montoBrutoBase, setMontoBrutoBase] = useState(solicitud?.total_cotizacion ? Math.round(Number(solicitud.total_cotizacion)) : 0);
  const [motivoDevolucion, setMotivoDevolucion] = useState('');
  const [aplicarRetencion, setAplicarRetencion] = useState(false);
  const [tasaRetencion, setTasaRetencion] = useState(15.25);

  useEffect(() => {
    // Obtener tasa de retención legal configurada
    api.get('/parametros/retencion')
      .then(res => {
        if (res.data?.tasa_retencion) {
          setTasaRetencion(Number(res.data.tasa_retencion));
        }
      })
      .catch(() => {
        setTasaRetencion(15.25);
      });
  }, []);

  const handleToggleRetencion = (checked) => {
    setAplicarRetencion(checked);
    const base = montoBrutoBase > 0 ? montoBrutoBase : (Number(montoDevolucion) || 0);
    setMontoBrutoBase(base);
    if (checked) {
      const retencion = Math.round(base * (tasaRetencion / 100));
      setMontoDevolucion(Math.max(0, base - retencion));
      if (!motivoDevolucion.includes('retención impositiva')) {
        const nota = ` (Aplica descuento por retención impositiva del ${tasaRetencion}% correspondiente al mes ya declarado al SII).`;
        setMotivoDevolucion(prev => prev ? `${prev.trim()}${nota}` : `Devolución de fondos.${nota}`);
      }
    } else {
      setMontoDevolucion(base);
    }
  };

  const montoRetencionCalculado = useMemo(() => {
    if (!aplicarRetencion) return 0;
    const base = montoBrutoBase > 0 ? montoBrutoBase : (Number(montoDevolucion) || 0);
    return Math.round(base * (tasaRetencion / 100));
  }, [aplicarRetencion, montoBrutoBase, montoDevolucion, tasaRetencion]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const numericMonto = Number(montoDevolucion);

    if (montoDevolucion === '' || isNaN(numericMonto) || numericMonto < 0) {
      toast.error('Ingrese un monto de devolución válido.');
      return;
    }

    if (!motivoDevolucion || motivoDevolucion.trim() === '') {
      toast.error('Ingrese el motivo de la devolución.');
      return;
    }

    const result = await Swal.fire({
      title: '¿Registrar Devolución?',
      text: `Se devolverá un monto de $${new Intl.NumberFormat('es-CL').format(numericMonto)} y la solicitud pasará a estado "Devuelto". Esta acción no se puede deshacer.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, registrar devolución',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) return;

    setLoading(true);
    try {
      await api.post(`/solicitudes/${solicitud.numero_seguimiento}/devolucion`, {
        monto_devolucion: numericMonto,
        motivo_devolucion: motivoDevolucion,
        aplicar_retencion: aplicarRetencion,
        tasa_retencion: tasaRetencion,
        monto_bruto: aplicarRetencion ? montoBrutoBase : numericMonto,
        monto_retencion: montoRetencionCalculado
      });
      toast.success('Devolución registrada exitosamente.');
      onUpdate();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al registrar la devolución.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: '520px', width: '95%', padding: 0, borderRadius: '12px', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header" style={{ padding: '1.2rem 1.5rem', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ padding: '8px', background: '#fee2e2', color: '#dc2626', borderRadius: '8px', display: 'flex' }}>
              <RotateCcw size={18} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-color, #0f172a)' }}>Registrar Devolución</h3>
              <span style={{ fontSize: '0.78rem', color: 'var(--gray-500)', fontFamily: 'monospace' }}>Solicitud #{solicitud.numero_seguimiento}</span>
            </div>
          </div>
          <button className="modal-close" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: 'var(--gray-500)' }}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: '1.5rem' }}>
            {/* Banner resumen */}
            <div style={{ marginBottom: '1.2rem', background: '#fef2f2', border: '1px solid #fecaca', padding: '12px 16px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <AlertCircle size={20} style={{ color: '#dc2626', flexShrink: 0 }} />
                <span style={{ fontSize: '0.85rem', color: '#991b1b', fontWeight: 500 }}>Total pagado originalmente:</span>
              </div>
              <span style={{ fontSize: '1.15rem', fontWeight: 800, color: '#dc2626' }}>
                ${new Intl.NumberFormat('es-CL').format(solicitud?.total_cotizacion || 0)}
              </span>
            </div>

            {/* Input monto a devolver */}
            <div className="form-group" style={{ marginBottom: '1.2rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label style={{ margin: 0, fontWeight: 600, fontSize: '0.85rem', color: 'var(--gray-700)' }}>
                  Monto {aplicarRetencion ? 'Líquido a Devolver' : 'a Devolver'} ($)
                </label>
                {solicitud?.total_cotizacion && (
                  <button
                    type="button"
                    onClick={() => {
                      const rounded = Math.round(Number(solicitud.total_cotizacion));
                      setMontoBrutoBase(rounded);
                      if (aplicarRetencion) {
                        const ret = Math.round(rounded * (tasaRetencion / 100));
                        setMontoDevolucion(Math.max(0, rounded - ret));
                      } else {
                        setMontoDevolucion(rounded);
                      }
                    }}
                    style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#047857', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', padding: '2px 8px', borderRadius: 4 }}
                  >
                    Restablecer total (${new Intl.NumberFormat('es-CL').format(solicitud.total_cotizacion)})
                  </button>
                )}
              </div>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontWeight: 700, fontSize: '1.2rem', color: 'var(--gray-400)' }}>$</span>
                <input 
                  type="text" 
                  className="form-control"
                  value={montoDevolucion !== '' && montoDevolucion !== null && !isNaN(montoDevolucion) ? new Intl.NumberFormat('es-CL').format(montoDevolucion) : ''}
                  onChange={(e) => {
                    const clean = e.target.value.replace(/\D/g, '');
                    const val = clean === '' ? '' : parseInt(clean, 10);
                    setMontoDevolucion(val);
                    if (!aplicarRetencion) {
                      setMontoBrutoBase(val || 0);
                    }
                  }}
                  placeholder="0"
                  required
                  style={{ fontSize: '1.25rem', fontWeight: 700, padding: '10px 14px 10px 30px', width: '100%', borderRadius: '8px', border: '1px solid var(--border-color, #cbd5e1)' }}
                />
              </div>
            </div>

            {/* Casilla de Retención Impositiva Ley 21.133 */}
            <div style={{
              marginBottom: '1.2rem',
              padding: '12px 14px',
              background: aplicarRetencion ? 'rgba(234, 88, 12, 0.06)' : '#f8fafc',
              border: `1px solid ${aplicarRetencion ? '#f97316' : '#e2e8f0'}`,
              borderRadius: '8px',
              transition: 'all 0.2s'
            }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', margin: 0 }}>
                <input 
                  type="checkbox"
                  checked={aplicarRetencion}
                  onChange={(e) => handleToggleRetencion(e.target.checked)}
                  style={{ width: '16px', height: '16px', marginTop: '2px', cursor: 'pointer', accentColor: '#ea580c' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '6px' }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: 700, color: aplicarRetencion ? '#c2410c' : '#1e293b' }}>
                      Descontar Retención Impositiva ({tasaRetencion}% - Ley 21.133)
                    </span>
                    <span style={{ fontSize: '0.72rem', background: '#ffedd5', color: '#9a3412', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>
                      Impuesto del mes declarado al SII
                    </span>
                  </div>
                  <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#64748b', lineHeight: 1.4 }}>
                    Marque esta opción si el impuesto del mes correspondiente ya fue declarado ante el SII. Se descontará automáticamente el {tasaRetencion}% legal.
                  </p>

                  {aplicarRetencion && (
                    <div style={{ marginTop: '10px', padding: '10px 12px', background: '#fff', borderRadius: '6px', border: '1px solid #fed7aa', fontSize: '0.82rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{ color: '#64748b' }}>Monto Bruto:</span>
                        <span style={{ fontWeight: 600 }}>${new Intl.NumberFormat('es-CL').format(montoBrutoBase)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', color: '#dc2626' }}>
                        <span>Retención Legal ({tasaRetencion}%):</span>
                        <span style={{ fontWeight: 600 }}>-${new Intl.NumberFormat('es-CL').format(montoRetencionCalculado)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '6px', borderTop: '1px dashed #e2e8f0', color: '#059669', fontWeight: 800 }}>
                        <span>Total Líquido a Devolver:</span>
                        <span>${new Intl.NumberFormat('es-CL').format(montoDevolucion)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </label>
            </div>

            {/* Motivo */}
            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.85rem', color: 'var(--gray-700)' }}>
                Motivo de la Devolución
              </label>
              <textarea 
                className="form-control"
                value={motivoDevolucion}
                onChange={(e) => setMotivoDevolucion(e.target.value)}
                placeholder="Indique la justificación formal de la devolución..."
                rows={3}
                required
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border-color, #cbd5e1)', fontSize: '0.88rem' }}
              />
            </div>
          </div>

          <div className="modal-footer" style={{ padding: '1rem 1.5rem', background: '#f8fafc', borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button type="button" className="btn btn-outline" onClick={onClose} disabled={loading} style={{ padding: '0.55rem 1.2rem', borderRadius: '8px' }}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-danger" disabled={loading} style={{ background: '#dc2626', color: 'white', border: 'none', padding: '0.55rem 1.4rem', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>
              {loading ? <span className="spinner" /> : 'Confirmar Devolución'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
